import React, {useMemo} from 'react';

import {DateFormat, format} from '../../../util/date';
import {type ReportMetadata, type TagV2} from '../../../util/gallery';
import * as Urls from '../../../util/urls';
import {TOPIC_COLORS} from '../config';
import {type TopicColor} from '../types';
import * as S from './styles';

export type PostCardProps = {
  report: ReportMetadata;
  className?: string;
  tagsV2?: TagV2[];
};

export const PostCard: React.FC<PostCardProps> = ({
  report,
  className,
  tagsV2,
}) => {
  const {id, displayName, description, addedAt, tags, project, entityName} =
    report;

  const link =
    project != null
      ? Urls.reportView({
          entityName: project.entityName,
          projectName: project.name,
          reportID: id,
          reportName: displayName,
        })
      : Urls.galleryPostView({
          entityName: entityName,
          reportID: id,
          reportName: displayName,
        });

  const displayTags = useMemo(() => {
    return (tags ?? []).filter(t => {
      if (t.name === 'Fully Connected') {
        return false;
      }
      if (tagsV2) {
        const fullTag = tagsV2.find(v2 => v2.name === t.name);
        return !!fullTag?.navCategory;
      }
      return true;
    });
  }, [tags, tagsV2]);

  const getTagColor = (tagName: string, tagColor?: string): TopicColor => {
    if (tagColor) {
      return {
        backgroundColor: tagColor,
        textColor: '#FFFFFF',
      };
    }
    return TOPIC_COLORS[tagName] || TOPIC_COLORS['default'];
  };

  let borderColor: string | undefined;
  if (!tagsV2 || !tags) {
    borderColor = undefined;
  } else {
    // Find the first tag on the report that is an Author tag
    const authorTag = tags.find(reportTag => {
      const fullTag = tagsV2.find(t => t.name === reportTag.name);
      return fullTag?.navCategory === 'Authors';
    });

    if (authorTag) {
      const fullTag = tagsV2.find(t => t.name === authorTag.name);
      if (fullTag?.color) {
        borderColor = fullTag.color;
      } else {
        borderColor = TOPIC_COLORS[authorTag.name]?.backgroundColor;
      }
    } else {
      borderColor = undefined;
    }
  }

  let formattedDate: string | null;
  if (!addedAt) {
    formattedDate = null;
  } else {
    formattedDate = format(new Date(addedAt), DateFormat.MONTH_DAY_YEAR);
  }

  return (
    <S.CardLink to={link} className={className}>
      <S.PostCardContainer $borderColor={borderColor}>
        <S.Content>
          <S.Header>
            {displayTags.length > 0 && (
              <S.Tags>
                {displayTags.slice(0, 3).map(tag => {
                  const colors = getTagColor(tag.name, tag.color);
                  return (
                    <S.TagBadge
                      key={tag.id}
                      $backgroundColor={colors.backgroundColor}
                      $textColor={colors.textColor}>
                      {tag.name}
                    </S.TagBadge>
                  );
                })}
              </S.Tags>
            )}
            <S.Title>{displayName}</S.Title>
            {description && <S.Description>{description}</S.Description>}
          </S.Header>

          <S.Meta>
            {/* Fallback to date if read time isn't available (it's not in ReportMetadata currently) */}
            {/* Ideally we would show "X mins read" here */}
            <S.ClockIcon name="recent-clock" />
            {formattedDate && <span>{formattedDate}</span>}
          </S.Meta>
        </S.Content>
      </S.PostCardContainer>
    </S.CardLink>
  );
};
